import React, { useEffect, useId, useMemo, useRef, useState } from "react";

type GoogleAddressInputProps = {
  id?: string;
  name?: string;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  style?: React.CSSProperties;
  autoComplete?: string;
  onChange: (value: string) => void;
  onPlaceSelected?: (value: string, place: GoogleAddressSuggestion) => void;
};

export type GoogleAddressSuggestion = {
  placeId: string;
  description: string;
  mainText?: string | null;
  secondaryText?: string | null;
};

export function GoogleAddressInput({
  id,
  name,
  value,
  placeholder = "Enter address",
  disabled,
  required,
  className,
  style,
  autoComplete = "off",
  onChange,
  onPlaceSelected,
}: GoogleAddressInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const generatedListId = useId();
  const listId = useMemo(() => `${id || generatedListId}-address-suggestions`, [generatedListId, id]);
  const [suggestions, setSuggestions] = useState<GoogleAddressSuggestion[]>([]);

  useEffect(() => {
    const query = value.trim();
    let cancelled = false;
    const controller = new AbortController();

    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    const timeout = window.setTimeout(async () => {
      try {
        const response = await fetch(`/api/v1/public/google-places/autocomplete?input=${encodeURIComponent(query)}`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          if (!cancelled) setSuggestions([]);
          return;
        }

        const body = await response.json();
        const nextSuggestions = Array.isArray(body?.data?.suggestions) ? body.data.suggestions : [];
        if (!cancelled) {
          setSuggestions(nextSuggestions);
        }
      } catch {
        if (!cancelled) {
          setSuggestions([]);
        }
      }
    }, 220);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [value]);

  const handleChange = (nextValue: string) => {
    onChange(nextValue);
    const selected = suggestions.find((suggestion) => suggestion.description === nextValue);
    if (selected) {
      onPlaceSelected?.(nextValue, selected);
    }
  };

  return (
    <>
      <input
        id={id}
        name={name}
        ref={inputRef}
        type="text"
        value={value}
        className={className}
        style={style}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        list={listId}
        onChange={(event) => handleChange(event.target.value)}
      />
      {suggestions.length > 0 ? (
        <datalist id={listId}>
          {suggestions.map((suggestion) => (
            <option key={suggestion.placeId} value={suggestion.description} />
          ))}
        </datalist>
      ) : null}
    </>
  );
}

export default GoogleAddressInput;
